import React, { useState, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";

export type PopoverPosition = "top" | "bottom" | "left" | "right";

export interface PopoverProps {
  message?: React.ReactNode;
  children: React.ReactNode;
  alwaysVisible?: boolean;
  messageClassName?: string;
  position?: PopoverPosition;
  shouldShowPopover?: boolean;
}

const Popover: React.FC<PopoverProps> = ({
  message,
  children,
  alwaysVisible = false,
  messageClassName,
  position = "right",
  shouldShowPopover = true
}) => {
  const [isVisible, setIsVisible] = useState(alwaysVisible);
  let timeoutId: NodeJS.Timeout | null = null;
  const triggerRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const handleMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    if (!alwaysVisible) {
      timeoutId = setTimeout(() => setIsVisible(false), 1000);
    }
  };

  const handleMessageMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const handleMessageMouseLeave = () => {
    if (!alwaysVisible) {
      timeoutId = setTimeout(() => setIsVisible(false), 1000);
    }
  };

  useLayoutEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const style: React.CSSProperties = { position: "absolute" };
      switch (position) {
        case "top":
          style.left = rect.left + rect.width / 2;
          style.top = rect.top;
          style.transform = "translate(-50%, -100%)";
          break;
        case "bottom":
          style.left = rect.left + rect.width / 2;
          style.top = rect.bottom;
          style.transform = "translate(-50%, 0)";
          break;
        case "left":
          style.left = rect.left;
          style.top = rect.top + rect.height / 2;
          style.transform = "translate(-100%, -50%)";
          break;
        case "right":
        default:
          style.left = rect.right;
          style.top = rect.top + rect.height / 2;
          style.transform = "translate(0, -50%)";
          break;
      }
      setPopoverStyle(style);
    }
  }, [isVisible, position]);



  return (
    <div className="relative" ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      {message && isVisible && shouldShowPopover && ReactDOM.createPortal(
        <div
          style={popoverStyle}
          className={`z-50 px-2 py-1 bg-gray-950 text-white rounded-lg shadow-lg whitespace-normal fixed`}
          onMouseEnter={handleMessageMouseEnter}
          onMouseLeave={handleMessageMouseLeave}
        >
          
          {position === "top" && (
            <svg className="absolute left-1/2" style={{ bottom: '-8px', transform: 'translateX(-50%)' }} width="16" height="8" viewBox="0 0 16 8"><polygon points="0,0 8,8 16,0" fill="#0a0a0a" /></svg>
          )}
          {position === "bottom" && (
            <svg className="absolute left-1/2 -top-2 -translate-x-1/2" width="16" height="8" viewBox="0 0 16 8"><polygon points="0,8 8,0 16,8" fill="#0a0a0a" /></svg>
          )}
          {position === "left" && (
            <svg className="absolute -right-2 top-1/2 -translate-y-1/2" width="8" height="16" viewBox="0 0 8 16"><polygon points="8,0 0,8 8,16" fill="#0a0a0a" /></svg>
          )}
          {position === "right" && (
            <svg className="absolute -left-2 top-1/2 -translate-y-1/2" width="8" height="16" viewBox="0 0 8 16"><polygon points="0,0 8,8 0,16" fill="#0a0a0a" /></svg>
          )}
          <div className={`break-all whitespace-normal overflow-auto ${messageClassName || ""}`}>{message}</div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Popover;
